/**
 * 数据 Provider - 管理应用数据获取和状态
 */

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAppStore } from '../stores/useAppStore';
import { useAuth } from './AuthProvider';

interface DataContextType {
  isDataLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  refreshMarketData: () => Promise<void>;
  refreshAccountData: () => Promise<void>;
  refreshDailyProfits: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 从认证 Provider 获取认证状态
  const { isAuthenticated } = useAuth();

  // 从 store 获取数据相关的方法
  const { fetchMarketData, fetchAccountData, fetchDailyProfits, fetchRecentPriceAlerts, initializeApp } = useAppStore();

  // 初始化数据
  const initializeData = useCallback(async () => {
    if (isLoading || isDataLoaded) {
      console.log('⏳ 数据正在加载中或已加载，跳过重复初始化');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log('🚀 开始初始化应用数据...');

      // 1. 初始化应用
      await initializeApp();

      // 2. 并行获取所有数据
      console.log('📊 并行获取所有数据...');
      const promises = [fetchMarketData(), fetchAccountData(), fetchDailyProfits(), fetchRecentPriceAlerts(24)];

      // 等待所有数据获取完成
      await Promise.allSettled(promises);

      console.log('✅ 所有数据初始化完成');
      setIsDataLoaded(true);
    } catch (error) {
      console.error('❌ 数据初始化失败:', error);
      setError(`数据加载失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading,
    isDataLoaded,
    fetchMarketData,
    fetchAccountData,
    fetchDailyProfits,
    fetchRecentPriceAlerts,
    initializeApp,
  ]);

  // 刷新所有数据
  const refreshData = async () => {
    setIsDataLoaded(false);
    await initializeData();
  };

  // 刷新特定数据
  const refreshMarketData = async () => {
    try {
      await fetchMarketData();
    } catch (error) {
      console.error('❌ 刷新市场数据失败:', error);
    }
  };

  const refreshAccountData = async () => {
    try {
      await fetchAccountData();
    } catch (error) {
      console.error('❌ 刷新账户数据失败:', error);
    }
  };

  const refreshDailyProfits = async () => {
    try {
      await fetchDailyProfits();
    } catch (error) {
      console.error('❌ 刷新每日收益数据失败:', error);
    }
  };

  // 当认证状态变化时，初始化数据
  useEffect(() => {
    if (isAuthenticated && !isDataLoaded && !isLoading) {
      console.log('🔐 用户已认证，开始初始化数据...');
      initializeData();
    } else if (!isAuthenticated) {
      // 用户未认证时，重置数据状态
      setIsDataLoaded(false);
      setError(null);
    }
  }, [isAuthenticated, isDataLoaded, isLoading, initializeData]);

  const value: DataContextType = {
    isDataLoaded,
    isLoading,
    error,
    refreshData,
    refreshMarketData,
    refreshAccountData,
    refreshDailyProfits,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

// Hook 用于使用数据上下文
export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
